import PropTypes from 'prop-types';
import { Card, Chip, Stack, Typography } from '@mui/material';
import CallAIReviewPanel from './CallAIReviewPanel';
import HighlightedText from './HighlightedText';
import { CARD_SX } from './CallKeyDetails';
import { highlightRanges, summaryCaption } from './callReviewModel';

const SENTIMENT_COLOR = { Positive: 'success', Negative: 'error', Neutral: 'default' };

// The summary with the extracted words highlighted, then Sentiment + Topics as labelled chips
// and any key points / concerns / next steps. `children` is shown when there is no summary
// (the Generate AI review controls).
export default function CallSummarySection({ call, review, extracted, children }) {
  const summary = typeof review?.summary === 'string' ? review.summary : '';
  const sentiment = ['Positive', 'Neutral', 'Negative'].includes(review?.sentiment) ? review.sentiment : null;
  const topics = Array.isArray(call?.aiTopics) ? call.aiTopics : [];
  return (
    <Card sx={CARD_SX} component="section" aria-label="Summary">
      <Typography variant="subtitle1">Summary</Typography>
      {summary ? (
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {summaryCaption(call)}
          </Typography>
          <Typography sx={{ fontSize: 16, lineHeight: 1.75, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }} data-testid="call-summary-text">
            <HighlightedText text={summary} ranges={highlightRanges(summary, extracted)} />
          </Typography>
          {(sentiment || topics.length > 0) && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              {sentiment && (
                <Chip size="small" variant="soft" color={SENTIMENT_COLOR[sentiment]} label={`Sentiment: ${sentiment}`} />
              )}
              {topics.map((topic) => (
                <Chip key={topic} size="small" variant="soft" label={`Topic: ${topic}`} />
              ))}
            </Stack>
          )}
          <CallAIReviewPanel review={review} listsOnly />
        </Stack>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {children}
        </Stack>
      )}
    </Card>
  );
}

CallSummarySection.propTypes = {
  call: PropTypes.object,
  review: PropTypes.object,
  extracted: PropTypes.object,
  children: PropTypes.node,
};
CallSummarySection.defaultProps = { call: null, review: null, extracted: null, children: null };
